import React, { Component } from 'react';
import classNames from 'classnames';
import { Modal } from '../../components';
import { withViewport } from '../../util/uiHelpers';

import css from './ModalInMobile.module.css';

/**
 * ModalInMobile gives possibility separate part of existing DOM so that in mobile views that
 * fragment is shown in a separate modal layer on top of the page.
 *
 * Currently, this does not implement resize listener for window.
 *
 * @example
 * <Parent>
 *   <ModalInMobile isModalOpenOnMobile={this.state.modalOpen} onClose={handleClose}>
 *     <FormX />
 *   </ModalInMobile>
 * </Parent>
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.modalContainer
 * @param {string} props.id
 * @param {boolean?} props.isModalOpenOnMobile
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {number?} props.showAsModalMaxWidth
 * @param {ReactNode?} props.closeButtonMessage
 * @param {'fullScreen' | 'bottomSheet'} [props.presentation] - Mobile modal layout variant
 * @param {boolean} [props.lightCloseButton] - Use lighter close button styles
 * @param {string?} props.scrollLayerClassName - Extra scroll layer class names
 * @param {Object} props.viewport
 * @param {number} props.viewport.width
 * @param {number} props.viewport.height
 * @returns {JSX.Element} container which is shown as a modal on mobile layout
 */
// Parent-driven bottom-sheet close animations should finish within this window.
// If isModalOpenOnMobile never flips, force-close so the overlay cannot stick open.
const BOTTOM_SHEET_CLOSE_FALLBACK_MS = 500;

class ModalInMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
    this.bottomSheetCloseFallback = null;
    this.handleClose = this.handleClose.bind(this);
    this.changeOpenStatus = this.changeOpenStatus.bind(this);
    this.clearBottomSheetCloseFallback = this.clearBottomSheetCloseFallback.bind(this);
  }

  componentDidMount() {
    const { isModalOpenOnMobile, showAsModalMaxWidth, viewport } = this.props;

    // After Mounting, component can adapt to responsive screen size
    const isMobileLayout = viewport.width <= showAsModalMaxWidth;

    if (isMobileLayout && isModalOpenOnMobile) {
      this.changeOpenStatus(isModalOpenOnMobile);
    }
  }

  componentDidUpdate() {
    const { isModalOpenOnMobile, showAsModalMaxWidth, viewport } = this.props;

    const isChanging = isModalOpenOnMobile !== this.state.isOpen;
    const isMobileLayout = viewport.width <= showAsModalMaxWidth;
    const shouldBeClosedAsModal = !isMobileLayout && !isModalOpenOnMobile;

    // Handle change if status is changing on mobile layout or it is closing (on desktop layout)
    if (isChanging && (isMobileLayout || shouldBeClosedAsModal)) {
      this.changeOpenStatus(isModalOpenOnMobile);
    }
  }

  componentWillUnmount() {
    this.clearBottomSheetCloseFallback();
  }

  clearBottomSheetCloseFallback() {
    if (this.bottomSheetCloseFallback) {
      clearTimeout(this.bottomSheetCloseFallback);
      this.bottomSheetCloseFallback = null;
    }
  }

  changeOpenStatus(isOpen) {
    if (!isOpen) {
      this.clearBottomSheetCloseFallback();
    }
    this.setState({ isOpen });
  }

  handleClose(event) {
    const { onClose, presentation } = this.props;
    // Bottom sheets may animate closed in the parent before isModalOpenOnMobile
    // flips. Closing local state here would hide the sheet before that can run.
    // Contract: onClose must eventually set isModalOpenOnMobile=false.
    if (presentation !== 'bottomSheet') {
      this.changeOpenStatus(false);
    } else {
      this.clearBottomSheetCloseFallback();
      this.bottomSheetCloseFallback = setTimeout(() => {
        this.bottomSheetCloseFallback = null;
        if (this.state.isOpen) {
          this.changeOpenStatus(false);
        }
      }, BOTTOM_SHEET_CLOSE_FALLBACK_MS);
    }
    if (onClose) {
      onClose(event);
    }
  }

  render() {
    const {
      children,
      className,
      rootClassName,
      containerClassName,
      id,
      showAsModalMaxWidth = 0,
      closeButtonMessage,
      onManageDisableScrolling,
      viewport,
      usePortal,
      presentation = 'fullScreen',
      lightCloseButton = false,
      scrollLayerClassName: scrollLayerClassNameProp,
    } = this.props;

    const isMobileLayout = viewport.width <= showAsModalMaxWidth;
    const isOpenInMobile = this.state.isOpen;
    const isClosedInMobile = isMobileLayout && !isOpenInMobile;
    const isOpen = isOpenInMobile && isMobileLayout;
    const isBottomSheet = presentation === 'bottomSheet';

    // We have 3 view states:
    // - default desktop layout (just an extra wrapper)
    // - mobile layout: content visible inside modal popup
    // - mobile layout: content hidden
    const closedClassName = isClosedInMobile ? css.modalHidden : null;
    const classes = classNames(
      rootClassName || css.root,
      className,
      isBottomSheet && css.bottomSheetRoot,
      isBottomSheet && isOpen && css.bottomSheetOpen
    );
    const scrollLayerClassName = classNames(
      isBottomSheet && css.bottomSheetScrollLayer,
      scrollLayerClassNameProp
    );
    const resolvedContainerClassName = classNames(
      containerClassName || css.modalContainer,
      isBottomSheet && css.bottomSheetContainer
    );

    return (
      <Modal
        className={classes}
        scrollLayerClassName={scrollLayerClassName}
        containerClassName={resolvedContainerClassName}
        contentClassName={classNames(css.modalContent, isBottomSheet && css.bottomSheetContent)}
        id={id}
        isOpen={isOpen}
        isClosedClassName={closedClassName}
        onClose={this.handleClose}
        closeButtonMessage={closeButtonMessage}
        lightCloseButton={lightCloseButton || isBottomSheet}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal={usePortal && isOpen}
      >
        {children}
      </Modal>
    );
  }
}

const ModalInMobile = withViewport(ModalInMobileComponent);

export default ModalInMobile;
